import { DateTime } from "luxon";
import type { StateStep } from "../../apiClient/generated";
import {
    actionText,
    formatDuration,
    formatStepTime,
    type PipelineStep,
    pipelineSteps,
    secondsBetween,
    stateLabel,
} from "./pipeline";
import { type DotTone, StatusDot } from "./StatusBadge";

const ACTION_TEXT_COLORS: Record<string, string> = {
    QUEUED: "text-ae-slate-600",
    RUNNING: "text-yellow-700",
    SUCCEEDED: "text-green-700",
    FAILED: "text-red-600",
};

const stepTone = (step: PipelineStep): DotTone => {
    if (step.problem === "failed" || step.state === "ERROR") {
        return "danger";
    }
    if (step.problem === "timedOut" || step.state === "TIMED_OUT") {
        return "warning";
    }
    if (step.isCurrent && !step.isTerminal) {
        return "active";
    }
    return "success";
};

const ProblemTag = ({ step }: { step: PipelineStep }) => {
    if (step.problem === "failed") {
        return (
            <span className="rounded-full border border-red-200 bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">
                Failed here
            </span>
        );
    }
    if (step.problem === "timedOut") {
        return (
            <span className="rounded-full border border-orange-200 bg-orange-50 px-2 py-0.5 text-xs font-medium text-orange-700">
                Timed out here
            </span>
        );
    }
    return null;
};

const StepDuration = ({ step, now }: { step: PipelineStep; now: Date }) => {
    if (step.isTerminal) {
        return null;
    }
    if (step.isCurrent) {
        return step.enteredAt ? (
            <span className="text-ae-slate-600">
                {formatDuration(secondsBetween(step.enteredAt, now))} so far
            </span>
        ) : null;
    }
    return <span>{formatDuration(step.durationSeconds)}</span>;
};

const StepAction = ({ step }: { step: PipelineStep }) => {
    const isRunningStep = step.isCurrent && !step.isTerminal;
    const text = actionText(step.actionStatus, isRunningStep);
    // A terminal state's action only reports or cleans up, so it's only worth
    // showing while it hasn't succeeded.
    if (!text || (step.isTerminal && step.actionStatus === "SUCCEEDED")) {
        return null;
    }
    const color =
        text === "Waiting"
            ? "text-ae-slate-600"
            : (ACTION_TEXT_COLORS[step.actionStatus] ?? "text-ae-slate-600");
    return <span className={color}>{text}</span>;
};

interface StepTimelineProps {
    steps: StateStep[];
    now?: Date;
}

/**
 * The steps a data import went through, oldest first: when each started, how
 * long it took and how its action ended. The current step is highlighted.
 */
export const StepTimeline = ({
    steps,
    now = new Date(),
}: StepTimelineProps) => {
    const items = pipelineSteps(steps);
    const reference = items[0]?.enteredAt ?? null;

    return (
        <ol className="flex flex-col" aria-label="Pipeline steps">
            {items.map((step, index) => (
                <li
                    key={`${step.state}-${index}`}
                    className="flex gap-3"
                    aria-current={step.isCurrent ? "step" : undefined}
                >
                    {/* Rail */}
                    <div className="flex flex-col items-center pt-2.5">
                        <StatusDot tone={stepTone(step)} />
                        {index < items.length - 1 && (
                            <div className="mt-1 w-px flex-1 bg-ae-gray-300" />
                        )}
                    </div>

                    {/* Content */}
                    <div
                        className={`mb-1 flex-1 rounded-md px-2 py-1.5 ${
                            step.isCurrent
                                ? "bg-ae-blue-50 ring-1 ring-ae-blue-80"
                                : ""
                        }`}
                    >
                        <div className="flex items-center justify-between gap-2">
                            <span
                                className={`flex items-center gap-2 ${
                                    step.isCurrent ? "font-bold" : "font-medium"
                                }`}
                                title={step.state}
                            >
                                {stateLabel(step.state)}
                                <ProblemTag step={step} />
                            </span>
                            <span className="shrink-0 tabular-nums">
                                <StepDuration step={step} now={now} />
                            </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-x-3 text-xs text-ae-slate-600">
                            <span
                                className="tabular-nums"
                                title={
                                    step.enteredAt
                                        ? DateTime.fromJSDate(
                                              step.enteredAt,
                                          ).toLocaleString(
                                              DateTime.DATETIME_FULL_WITH_SECONDS,
                                          )
                                        : undefined
                                }
                            >
                                {step.isCurrent && step.isTerminal
                                    ? "Reached"
                                    : "Started"}{" "}
                                {formatStepTime(step.enteredAt, reference)}
                            </span>
                            <StepAction step={step} />
                        </div>
                    </div>
                </li>
            ))}
        </ol>
    );
};
